"use client";
import { AppRoutes, isGroupsHref } from "@/lib/routeResolver";

import { useState, useSyncExternalStore, type ReactNode } from "react";
import { createPortal } from "react-dom";
import Link from "@/components/Link";
import Image from "next/image";
import type { NavigationData } from "@/lib/navigation";
import { sanitizeProjectURL } from "@/lib/helpers";
import CountryFlag from "@/app/(main)/components/CountryFlag";

interface MobileMenuProps {
  nav: NavigationData;
}

/**
 * A collapsible parent item — closed by default, toggles its sub-menu on tap.
 * Replaces the old jQuery `thmobilemenu` behaviour, which raced this React
 * portal's mount and fought React's DOM, leaving submenus stuck open and
 * un-closable. The `.th-active` class drives the theme's +/- icon; the
 * sub-menu's visibility is controlled here so it's fully deterministic.
 */
function MobileSubMenu({
  label,
  children,
  liClassName,
}: {
  label: ReactNode;
  children: ReactNode;
  liClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <li
      className={`menu-item-has-children${open ? " th-active" : ""}${
        liClassName ? ` ${liClassName}` : ""
      }`}
    >
      <a
        href="#"
        aria-expanded={open}
        title={typeof label === "string" ? label : undefined}
        onClick={(e) => {
          e.preventDefault();
          setOpen((o) => !o);
        }}
      >
        {label}
      </a>
      <ul className="sub-menu" style={{ display: open ? "block" : "none" }}>
        {children}
      </ul>
    </li>
  );
}

export default function MobileMenu({ nav }: MobileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  // Defer the off-canvas portal until after mount so SSR (which renders no
  // portal) and the first client render agree — avoids a hydration mismatch
  // without a setState-in-effect.
  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );

  const close = () => setIsOpen(false);

  const {
    destination,
    reginData,
    resRegionsWithToursData,
    cruiseRegionsData,
    pageData,
    pagecontainerData,
    company,
  } = nav;

  const sidebar = (
    <div
      className={`th-menu-wrapper onepage-nav${isOpen ? " th-body-visible" : ""}`}
      // Clicking the dimmed backdrop (the wrapper itself, not the inner panel)
      // closes the menu.
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="th-menu-area text-center">
        <button className="th-menu-toggle" onClick={close}>
          <i className="fal fa-times"></i>
        </button>
        <div className="mobile-logo">
          <Link
            href={AppRoutes.home()}
            onClick={close}
            title="Central Holidays — Home"
          >
            <Image
              width={220}
              height={60}
              src={company?.logo || "/main/img/central.png"}
              alt="Central Holidays"
              style={{ width: "auto", height: "auto" }}
            />
          </Link>
        </div>
        {/* Keyed on open state so each time the menu is opened the submenus
            start fresh (all collapsed), regardless of prior expansions. */}
        <div className="th-mobile-menu" key={isOpen ? "open" : "closed"}>
          <ul>
            <li>
              <Link
                href={AppRoutes.home()}
                onClick={close}
                title="Central Holidays home page"
              >
                Home
              </Link>
            </li>

            {/* Destinations */}
            <MobileSubMenu label="Destinations">
              {destination?.map((dest) => {
                if (!dest.isNavShown) return null;
                const region = reginData?.find((r) =>
                  r.countriesId.includes(dest.destinationId),
                );
                if (!region) return null;
                const idx = region.countriesId.indexOf(dest.destinationId);
                const countryName = region.countriesNames[idx];
                return (
                  <li key={dest.id}>
                    <Link
                      href={AppRoutes.destination(dest.pageURL)}
                      onClick={close}
                      title={countryName}
                    >
                      <CountryFlag country={countryName} />
                      {countryName}
                    </Link>
                  </li>
                );
              })}
              {reginData?.map((region) => (
                <MobileSubMenu key={region.id} label={region.name}>
                  {region.countriesId.map((id, i) => (
                    <li key={id}>
                      <Link
                        href={
                          region.countriesURLs[i]
                            ? `/destination/${region.countriesURLs[i]}`
                            : "/"
                        }
                        onClick={close}
                        title={region.countriesNames[i]}
                      >
                        <CountryFlag country={region.countriesNames[i]} />
                        {region.countriesNames[i]}
                      </Link>
                    </li>
                  ))}
                </MobileSubMenu>
              ))}
            </MobileSubMenu>

            {/* Experiences */}
            {resRegionsWithToursData?.length > 0 && (
              <MobileSubMenu label="Experiences">
                <li>
                  <Link
                    href={AppRoutes.experiences()}
                    onClick={close}
                    title="Browse all experiences"
                  >
                    All Experiences
                  </Link>
                </li>
                {resRegionsWithToursData.map((region) =>
                  region.destinations?.length ? (
                    <MobileSubMenu
                      key={region.regionId}
                      label={region.regionName}
                    >
                      {region.destinations.map((dest) => (
                        <li key={dest.destinationId}>
                          <Link
                            href={AppRoutes.experienceDestination(
                              dest.destinationPageURL,
                            )}
                            onClick={close}
                            title={dest.destinationName}
                          >
                            {dest.destinationName}
                          </Link>
                        </li>
                      ))}
                    </MobileSubMenu>
                  ) : (
                    <li key={region.regionId}>
                      <Link
                        href={AppRoutes.experienceRegion(region.regionPageURL)}
                        onClick={close}
                        title={region.regionName}
                      >
                        {region.regionName}
                      </Link>
                    </li>
                  ),
                )}
              </MobileSubMenu>
            )}

            {/* Cruises (region → destination) */}
            {cruiseRegionsData?.length > 0 && (
              <MobileSubMenu label="Cruises">
                <li>
                  <Link
                    href={AppRoutes.cruises()}
                    onClick={close}
                    title="Browse all cruises"
                  >
                    All Cruises
                  </Link>
                </li>
                {cruiseRegionsData.map((region) =>
                  region.countries?.length ? (
                    <MobileSubMenu
                      key={region.regionId}
                      label={region.regionName}
                    >
                      {region.countries.map((country) => (
                        <li key={country.countryId}>
                          <Link
                            href={AppRoutes.cruiseDestination(
                              region.pageUrl,
                              country.pageUrl,
                            )}
                            onClick={close}
                            title={country.countryName}
                          >
                            <CountryFlag
                              country={country.countryName}
                              fallback={null}
                            />
                            {country.countryName}
                          </Link>
                        </li>
                      ))}
                    </MobileSubMenu>
                  ) : (
                    <li key={region.regionId}>
                      <Link
                        href={AppRoutes.cruiseRegion(region.pageUrl)}
                        onClick={close}
                        title={region.regionName}
                      >
                        {region.regionName}
                      </Link>
                    </li>
                  ),
                )}
              </MobileSubMenu>
            )}

            {/* Dynamic Pages */}
            {pageData
              ?.filter(
                (page) => page.isHeaderPage && page.name !== "Swan Hellenic",
              )
              .map((page) => {
                const container = pagecontainerData.find(
                  (c) =>
                    c.pageModuleId === page.id &&
                    (!c.pageSubModuleId || c.pageSubModuleId.length < 1),
                );
                if (!container) return null;

                let href = "/";
                let target: string | undefined;

                if (container.isExtrnalLink) {
                  href = container.externalLinkUrl || "/";
                  target = container.target || "_blank";
                } else if (container.isPdfUrl) {
                  href = container.pdfUrl || "/";
                  target = "_blank";
                } else {
                  href = `/${
                    container.pageSEO?.pageURL || sanitizeProjectURL(page.name)
                  }`;
                }

                return (
                  <li key={page.id}>
                    {target ? (
                      <a
                        href={href}
                        target={target}
                        rel="noreferrer"
                        title={page.name}
                      >
                        {page.name}
                      </a>
                    ) : isGroupsHref(href) ? (
                      // Groups microsite is a separate route group with its
                      // own layout → full reload, not client nav.
                      <a href={href} title={page.name}>
                        {page.name}
                      </a>
                    ) : (
                      <Link href={href} onClick={close} title={page.name}>
                        {page.name}
                      </Link>
                    )}
                  </li>
                );
              })}
          </ul>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {mounted && createPortal(sidebar, document.body)}

      {/* Mobile Toggle Button */}
      <button
        type="button"
        className="th-menu-toggle d-block d-xl-none"
        onClick={() => setIsOpen(true)}
      >
        <i className="far fa-bars"></i>
      </button>
    </>
  );
}
